import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { isSkipGlobalError, isAuthSessionRequest } from "@/lib/http/skip-global-error";
import { detectBrowserLang, normalizeLang, translate, SUPPORTED_LANGS } from "./locales";
import { getApiBaseUrl } from "@/lib/api-base";
import { loadTriSettings, saveTriSettings, DEFAULT_TRI_SETTINGS } from "@/lib/i18n/tri-language";

const STORAGE_KEY = "vedaPath_lang";

const I18nContext = createContext(null);

const API = getApiBaseUrl();

export function I18nProvider({ children, user, token, updateProfile }) {
  const [lang, setLangState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return normalizeLang(stored || detectBrowserLang());
  });
  const [titles, setTitles] = useState(null);
  const [globalError, setGlobalError] = useState(null);
  const [triSettings, setTriSettingsState] = useState(() => loadTriSettings());

  const updateTriSettings = useCallback((patch) => {
    const next = saveTriSettings(patch);
    setTriSettingsState(next);
    if (patch.preferredLanguage) {
      setLangState(normalizeLang(patch.preferredLanguage));
      localStorage.setItem(STORAGE_KEY, normalizeLang(patch.preferredLanguage));
    }
    return next;
  }, []);

  const setLang = useCallback(
    async (next) => {
      const code = normalizeLang(next);
      setLangState(code);
      localStorage.setItem(STORAGE_KEY, code);
      saveTriSettings({ preferredLanguage: code });
      setTriSettingsState((s) => ({ ...s, preferredLanguage: code }));
      if (token && updateProfile) {
        try {
          await updateProfile({ language: code, panchanga_prefs: { language: code } });
        } catch (e) {
          console.error("language sync failed", e);
        }
      }
    },
    [token, updateProfile]
  );

  useEffect(() => {
    if (user?.language) {
      const code = normalizeLang(user.language);
      setLangState(code);
      localStorage.setItem(STORAGE_KEY, code);
    }
  }, [user?.language]);

  useEffect(() => {
    axios
      .get(`${API}/i18n/titles?lang=${encodeURIComponent(lang)}`)
      .then((r) => setTitles(r.data))
      .catch(() => {});
  }, [lang]);

  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use((config) => {
      config.params = { ...config.params, lang };
      return config;
    });

    const resInterceptor = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (isSkipGlobalError(error.config)) {
          return Promise.reject(error);
        }
        const status = error.response?.status;
        if (isAuthSessionRequest(error.config) && (status === 401 || status === 403)) {
          return Promise.reject(error);
        }
        const offline = typeof navigator !== "undefined" && !navigator.onLine;
        const dharma = error.response?.data?.dharma_error;
        const detail =
          dharma?.detail ||
          error.response?.data?.detail ||
          (offline ? translate(lang, "error.offline_body") : translate(lang, "error.generic"));
        if (
          status === 401 &&
          typeof detail === "string" &&
          /not authenticated/i.test(detail)
        ) {
          return Promise.reject(error);
        }

        setGlobalError({
          title: dharma?.title || (offline ? translate(lang, "error.offline_title") : "Pause"),
          detail,
          retry: dharma?.retry_suggestion || translate(lang, "btn.retry"),
          offline,
          code: dharma?.code || (offline ? "offline" : "network"),
          offline_guidance: dharma?.offline_guidance,
        });
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
    };
  }, [lang]);

  const t = useCallback((key) => translate(lang, key), [lang]);

  const isRtl = lang === "ar";
  const fallbackLang = "en";

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir = isRtl ? "rtl" : "ltr";
    }
  }, [lang, isRtl]);

  const value = useMemo(
    () => ({
      lang,
      fallbackLang,
      isRtl,
      setLang,
      t: (key) => {
        const v = translate(lang, key);
        if (v === key && lang !== fallbackLang) return translate(fallbackLang, key);
        return v;
      },
      titles,
      supportedLangs: SUPPORTED_LANGS,
      triSettings: { ...DEFAULT_TRI_SETTINGS, preferredLanguage: lang, ...triSettings },
      updateTriSettings,
      globalError,
      clearError: () => setGlobalError(null),
    }),
    [lang, setLang, titles, globalError, fallbackLang, isRtl, triSettings, updateTriSettings]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
